import { useCallback, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Flags } from '@anyroad/flags';
import { LocationAnswer } from '@anyroad/google-maps';
import Dimmer from 'views/shared/ui/dimmer';
import { formatBookerGuestName } from 'views/plugins/checkout/guest_information/Helpers';
import { sendEventToSegment } from 'views/anyguide/helpers/analytics/segmentHelpers';
import { getSessionId } from 'views/plugins/tours/page/booking_details/Helpers';
import { Theme } from 'ui_system/theme';
import {
  calculateMissingGuestEntries,
  getBookerUnitId,
} from './guest_information/Helpers';
import { hasLocationAnswer } from './Helpers';

import { useCheckout } from './CheckoutContext';
import { createPageOneValidationSchema } from './pageOneValidation';
import type {
  PersonalInfoFormData,
  GuestInfoFormData,
  AdditionalInfoFormData,
  CheckoutPageOneProps,
} from './types';

import { LanguageHeader, LanguageSelector } from '../default/header';
import { BookingTimer } from './booking_timer';
import { Header, PartnerHeader } from './header';
import { Summary } from './summary';
import { PersonalInformation } from './personal_information';
import { GuestInformation } from './guest_information';
import { AdditionalInformation } from './additional_information';
import { PrivacyPolicy } from './privacy_policy';
import { PoweredBy } from './powered-by';
import { transformCheckoutQuestionsErrors } from './questions/Helpers';
import {
  PageContainer,
  SummaryWrapper,
  ColumnContainer,
  ComponentContainer,
  FullBleed,
  LiveBookingHeader,
  AffiliateFreeBookingMessage,
  ButtonContainer,
  SubmitButton,
  Divider,
} from './styles';
import { isGuestOnList } from '../api';

export const CheckoutPageOne = ({
  pageOneData,
  onSubmit,
}: CheckoutPageOneProps) => {
  const { formatMessage } = useIntl();
  const context = useCheckout();
  const {
    flags,
    summaryProps,
    pricingData,
    tourId,
    bookingRequestId,
    isBookingInvoice,
    isLiveBooking,
    isAffiliate,
    isUnpaidBooking,
    phoneMandatory,
    isGuestInfoCollectionEnabled,
    isGuestOfGuestEnabled,
    guestOfGuestMaxGuests,
    units,
    partnerInfo,
    willExpireOn,
    i18nList,
    i18nLang,
    zeroPricedTour,
    bookerUnitId,
    setBookerUnitId,
    privacyPolicyLink,
    privacyPolicyLinkText,
    tourSettings,
    loading,
    isRsvpEnabled,
    segmentTrackedPages,
    markSegmentTrackedPage,
  } = context;

  useEffect(() => {
    if (!bookingRequestId) return;
    if (segmentTrackedPages.pageOne) return;
    if (!tourId) return;
    if (!flags?.[Flags['event-tracking-checkout-02112026-enabled']]) return;

    const guideId = summaryProps?.request?.guide_id;
    const guideName = summaryProps?.request?.guide_full_name;
    const device = Theme.mobile.isMobile ? 'mobile' : 'web';

    try {
      sendEventToSegment('Arrive on page', {
        path: window.location.hash || window.location.pathname,
        referrer: document.referrer,
        session_id: getSessionId(),
        page_name: 'Checkout Started',
        url: window.location.href,
        checkout_flow: 'mobile_first',
        experience_id: tourId,
        account_id: guideId,
        account_name: guideName,
        device,
      });
      markSegmentTrackedPage(1);
    } catch (error) {
      console.log('segment tracking error', error);
    }
  }, [
    bookingRequestId,
    flags,
    markSegmentTrackedPage,
    segmentTrackedPages.pageOne,
    summaryProps,
    tourId,
  ]);

  const hasQuestions = useMemo(() => {
    const requestQuestions = (summaryProps as any).checkout_questions || [];
    return Array.isArray(requestQuestions) && requestQuestions.length > 0;
  }, [summaryProps]);

  const shouldShowQuestions = useMemo(() => {
    return hasQuestions;
  }, [hasQuestions]);

  const shouldShowWaiver = useMemo(() => {
    return (
      !isAffiliate && summaryProps.request.tour_waiver_info?.required === true
    );
  }, [isAffiliate, summaryProps]);

  const shouldShowAdditionalInfo = useMemo(() => {
    const hasContent = shouldShowQuestions || shouldShowWaiver;
    return hasContent;
  }, [shouldShowQuestions, shouldShowWaiver]);

  const shouldValidatePrivacyPolicy = useMemo(() => {
    const affiliatePayLaterCheckout = isAffiliate && isUnpaidBooking;
    return (
      privacyPolicyLink && privacyPolicyLinkText && !affiliatePayLaterCheckout
    );
  }, [isAffiliate, isUnpaidBooking, privacyPolicyLink, privacyPolicyLinkText]);

  const shouldCollectGuestInfo = useMemo(() => {
    const numOfGuests = units.reduce((a, e) => a + e.count, 0);
    const ignoreMaxGuest =
      guestOfGuestMaxGuests === 0 ? true : numOfGuests <= guestOfGuestMaxGuests;
    return (
      !isAffiliate && isGuestOfGuestEnabled && numOfGuests > 1 && ignoreMaxGuest
    );
  }, [units, isAffiliate, isGuestOfGuestEnabled, guestOfGuestMaxGuests]);

  const fieldsToHide = useMemo(() => {
    const hide: string[] = [];
    if (isLiveBooking) return hide;
    const isInvoice = summaryProps.request.is_invoice;
    if (isInvoice && summaryProps.request.email) {
      hide.push('email');
    }
    if (isInvoice) {
      hide.push('name');
    }
    return hide;
  }, [isLiveBooking, summaryProps]);

  const validationSchema = useMemo(
    () =>
      createPageOneValidationSchema(formatMessage, {
        phoneMandatory,
        showZipCodeOnLive:
          summaryProps.request.live?.activate_zip_code_field || false,
        isLiveBooking,
        isGuestInfoCollectionEnabled,
        shouldCollectGuestInfo,
        isAffiliate,
        fieldsToHide,
        shouldShowWaiver,
        shouldShowQuestions,
        questions: summaryProps?.checkout_questions || [],
        shouldValidatePrivacyPolicy: Boolean(shouldValidatePrivacyPolicy),
      }),
    [
      formatMessage,
      phoneMandatory,
      summaryProps,
      isLiveBooking,
      isGuestInfoCollectionEnabled,
      shouldCollectGuestInfo,
      isAffiliate,
      fieldsToHide,
      shouldShowWaiver,
      shouldShowQuestions,
      shouldValidatePrivacyPolicy,
    ]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
    getValues,
    watch,
  } = useForm<
    PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
  >({
    resolver: yupResolver(validationSchema),
    defaultValues: context.pageOneData || {
      first_name: pageOneData?.first_name || '',
      last_name: pageOneData?.last_name || '',
      email: pageOneData?.email || '',
      phone: pageOneData?.phone || '',
      zip_code: '',
      additional_guests: [],
      booker_unit_id: bookerUnitId ?? undefined,
      checkout_questions: summaryProps?.checkout_questions || [],
      waiver_accepted: false,
      privacy_policy_accepted: false,
    },
    mode: 'onBlur',
  });

  const formValues = watch();

  useEffect(() => {
    if (!shouldCollectGuestInfo || !units || units.length === 0) {
      return;
    }

    if (bookerUnitId == null) {
      const defaultBookerUnitId = getBookerUnitId(units);
      if (defaultBookerUnitId) {
        setBookerUnitId(defaultBookerUnitId);
        setValue('booker_unit_id', defaultBookerUnitId);
      }
    }

    const currentGuests = getValues('additional_guests') || [];
    const newGuests = calculateMissingGuestEntries(
      units,
      bookerUnitId,
      currentGuests
    );

    if (newGuests.length > 0) {
      setValue('additional_guests', [...currentGuests, ...newGuests], {
        shouldValidate: false,
      });
    }
  }, [units, bookerUnitId, shouldCollectGuestInfo, setValue, getValues]);

  const questions = useMemo(() => {
    const requestQuestions = (summaryProps as any).checkout_questions || [];
    const baseQuestions = Array.isArray(requestQuestions)
      ? requestQuestions
      : [];
    const formQuestionAnswers = formValues.checkout_questions || [];

    return baseQuestions.map((question: any) => {
      const formAnswer = formQuestionAnswers.find(
        (qa: any) => qa.position === question.position
      );
      return {
        ...question,
        answer: formAnswer ? formAnswer.answer : question.answer,
        ...(hasLocationAnswer(formAnswer?.location_answer) && {
          location_answer: formAnswer.location_answer,
        }),
      };
    });
  }, [summaryProps, formValues.checkout_questions]);

  const handleFormSubmit = useCallback(
    async (data: any) => {
      const formData = data as PersonalInfoFormData &
        GuestInfoFormData &
        AdditionalInfoFormData;
      if (isRsvpEnabled) {
        const isGuestOnListResult = (await isGuestOnList(
          tourId,
          data.email
        )) as boolean;
        if (!isGuestOnListResult) {
          setError('email', {
            message: formatMessage({
              id: 'RESTRICTED_BOOKING_EMAIL_ERROR',
              defaultMessage:
                'Email address is not on the approved guest list.',
            }),
          });
          return;
        }
      }
      onSubmit(formData);
    },
    [onSubmit, tourId]
  );

  const handleFormError = useCallback((validationErrors: any) => {
    console.error('Form validation errors:', validationErrors);
  }, []);

  const handleAdditionalGuestChange = useCallback(
    (
      position: number,
      value: string,
      nameField: string,
      unitTypeId: number
    ) => {
      if (position === 0 && nameField === 'bookerUnitId') {
        setBookerUnitId(parseInt(value, 10));
        setValue('booker_unit_id', parseInt(value, 10));
        return;
      }

      const currentGuests = formValues.additional_guests || [];
      const guestIndex = currentGuests.findIndex(
        (g) => g.position === position
      );

      if (guestIndex === -1) {
        const newGuest = {
          position,
          [nameField]: value,
          unit_type_id: unitTypeId,
        };
        setValue('additional_guests', [...currentGuests, newGuest]);
      } else {
        const updatedGuests = [...currentGuests];
        updatedGuests[guestIndex] = {
          ...updatedGuests[guestIndex],
          [nameField]: value,
          unit_type_id: unitTypeId,
        };
        setValue('additional_guests', updatedGuests);
      }
    },
    [formValues.additional_guests, setValue, setBookerUnitId]
  );

  const handleWaiverChange = useCallback(
    (value: boolean) => {
      setValue('waiver_accepted', value, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleQuestionChange = useCallback(
    (
      position: number,
      value:
        | string
        | number
        | boolean
        | (string | number)[]
        | LocationAnswer
        | null
    ) => {
      const currentQuestions = formValues.checkout_questions || [];
      const questionIndex = currentQuestions.findIndex(
        (q) => q.position === position
      );

      if (value === null) {
        if (questionIndex === -1) {
          return;
        }
        const updatedQuestions = [...currentQuestions];
        updatedQuestions[questionIndex] = {
          ...updatedQuestions[questionIndex],
          answer: '',
          location_answer: null,
        };
        setValue('checkout_questions', updatedQuestions);
        return;
      }

      const locationAnswer = hasLocationAnswer(value)
        ? (value as LocationAnswer)
        : undefined;

      const answerValue: string | string[] = locationAnswer
        ? locationAnswer.raw_address
        : Array.isArray(value)
          ? value.map((v) => v.toString())
          : value.toString();

      if (questionIndex === -1) {
        const requestQuestions = (summaryProps as any).checkout_questions || [];
        const baseQuestion = Array.isArray(requestQuestions)
          ? requestQuestions.find((q: any) => q.position === position)
          : null;
        const tour_question_id =
          baseQuestion?.tour_question_id || baseQuestion?.id;

        if (!tour_question_id) {
          console.error(
            `Unable to find tour_question_id for question at position ${position}`
          );
          return;
        }

        setValue('checkout_questions', [
          ...currentQuestions,
          {
            position,
            tour_question_id,
            answer: answerValue,
            ...(hasLocationAnswer(locationAnswer) && {
              location_answer: locationAnswer,
            }),
          },
        ]);
      } else {
        const updatedQuestions = [...currentQuestions];
        updatedQuestions[questionIndex] = {
          ...updatedQuestions[questionIndex],
          answer: answerValue,
          ...(hasLocationAnswer(locationAnswer) && {
            location_answer: locationAnswer,
          }),
        };
        setValue('checkout_questions', updatedQuestions);
      }
    },
    [formValues.checkout_questions, setValue, summaryProps]
  );

  const checkoutSteps = useMemo(() => {
    const steps = [
      {
        id: 'personalInfo',
        shouldShow: isGuestInfoCollectionEnabled,
        stepNo: null as number | null,
      },
      {
        id: 'guestInfo',
        shouldShow: shouldCollectGuestInfo && !isBookingInvoice,
        stepNo: null as number | null,
      },
      {
        id: 'additionalInfo',
        shouldShow: shouldShowAdditionalInfo,
        stepNo: null as number | null,
      },
    ];

    const visibleSteps = steps.filter((step) => step.shouldShow);
    visibleSteps.forEach((step, index) => {
      step.stepNo = index + 1;
    });

    return {
      steps: visibleSteps,
      getStepNo: (stepId: string) => {
        const step = visibleSteps.find((s) => s.id === stepId);
        return step?.stepNo ?? null;
      },
    };
  }, [
    isGuestInfoCollectionEnabled,
    shouldCollectGuestInfo,
    isBookingInvoice,
    shouldShowAdditionalInfo,
  ]);

  const renderPartnerHeader = () => {
    const affiliatePayLaterCheckout = isAffiliate && isUnpaidBooking;
    if ((!isBookingInvoice && !isLiveBooking) || affiliatePayLaterCheckout) {
      return null;
    }

    return (
      <PartnerHeader
        isLiveHeader={isLiveBooking}
        hideOnMobile
        bgColor={partnerInfo.header_color}
        title={partnerInfo.business_name}
        logo={partnerInfo.logo}
      />
    );
  };

  const summaryPropsFormatted = useMemo(
    () => ({
      request: summaryProps.request as any,
      tour_id: tourId?.toString() || '',
      location: summaryProps.location || { label: '', value: '' },
      pricingData,
    }),
    [summaryProps, tourId, pricingData]
  );

  const bookerGuestName = useMemo(() => {
    return formatBookerGuestName({
      firstName: formValues.first_name || '',
      lastName: formValues.last_name || '',
    });
  }, [formValues.first_name, formValues.last_name]);

  const checkoutPageCssClasses = classnames(
    'CheckoutPage CheckoutMobileFirstPage',
    {
      'is-live': isLiveBooking,
    }
  );

  const showBookingTimer = !isBookingInvoice && !isLiveBooking;

  const partnerHeader = renderPartnerHeader();

  const renderSubmitButtonLabel = () => {
    const settings = tourSettings;
    const isInvoice = summaryProps.request.is_invoice;

    if (isInvoice) {
      return (
        settings?.submit_invoice_label?.value ||
        formatMessage({ id: 'SUBMIT', defaultMessage: 'Submit' })
      );
    }

    if (isLiveBooking) {
      return formatMessage({ id: 'REGISTER', defaultMessage: 'Register' });
    }

    if (zeroPricedTour) {
      return (
        settings?.submit_booking_label?.value ||
        formatMessage({ id: 'SUBMIT', defaultMessage: 'Submit' })
      );
    }

    const label = settings?.payment_booking_label?.value;
    return label === 'Proceed to payment'
      ? formatMessage({ defaultMessage: 'Proceed to payment' })
      : label || formatMessage({ defaultMessage: 'Proceed to payment' });
  };

  return (
    <PageContainer className={checkoutPageCssClasses}>
      <Dimmer fixed transparent loader active={loading} />
      {partnerHeader && <FullBleed>{partnerHeader}</FullBleed>}
      {isLiveBooking && i18nList.length > 0 && (
        <FullBleed>
          <LanguageHeader>
            <LanguageSelector
              value={{ value: i18nLang, label: i18nLang }}
              options={i18nList.map((lang) => ({ value: lang, label: lang }))}
              onChange={() => {}}
            />
          </LanguageHeader>
        </FullBleed>
      )}

      <form onSubmit={handleSubmit(handleFormSubmit, handleFormError)}>
        <ColumnContainer className="MobileFirstColumnContainer">
          {!isLiveBooking && (
            <SummaryWrapper>
              <ComponentContainer className="ComponentContainer MobileFirstSummaryColumn">
                <Summary {...summaryPropsFormatted} />
              </ComponentContainer>
            </SummaryWrapper>
          )}

          <ComponentContainer className="ComponentContainer MobileFirstColumn">
            {showBookingTimer && <Header />}

            {showBookingTimer && willExpireOn && (
              <BookingTimer
                sessionExpired={willExpireOn}
                tourId={tourId}
                guideId={summaryProps.request.guide_id}
                originUrl={summaryProps.request.origin_url}
              />
            )}

            {isLiveBooking && (
              <LiveBookingHeader>
                <h3>{summaryProps.request.tour_name_translation}</h3>
                <p>
                  <FormattedMessage
                    defaultMessage="With {businessName}"
                    values={{ businessName: partnerInfo.business_name }}
                  />
                </p>
              </LiveBookingHeader>
            )}

            {isGuestInfoCollectionEnabled && (
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <PersonalInformation
                    fields={{
                      get: (fieldName: string) => {
                        if (fieldName === 'first_name') return field.value;
                        return (
                          (formValues[
                            fieldName as keyof typeof formValues
                          ] as string) || ''
                        );
                      },
                    }}
                    errors={{
                      get: (fieldName: string) => {
                        const error = errors[fieldName as keyof typeof errors];
                        return error ? [error.message || ''] : undefined;
                      },
                    }}
                    onChange={(fieldName: string, value: string) => {
                      setValue(fieldName as any, value, {
                        shouldValidate: true,
                      });
                    }}
                    borderless={
                      zeroPricedTour === null ||
                      (zeroPricedTour && !shouldCollectGuestInfo)
                    }
                    fieldsToHide={fieldsToHide}
                    is_booking_invoice={isBookingInvoice}
                    isLiveBooking={isLiveBooking}
                    isAffiliate={isAffiliate}
                    phoneMandatory={phoneMandatory}
                    showZipCodeOnLive={
                      summaryProps.request.live?.activate_zip_code_field
                    }
                    countryCode="US"
                    emailSubtext={summaryProps.request.email_subtext}
                    phoneSubtext={summaryProps.request.phone_subtext}
                    fieldRequirementsSubtext={
                      summaryProps.request.field_requirements_subtext
                    }
                  />
                )}
              />
            )}

            {isAffiliate &&
              zeroPricedTour !== null &&
              !isGuestInfoCollectionEnabled &&
              zeroPricedTour && (
                <AffiliateFreeBookingMessage>
                  <FormattedMessage defaultMessage="Affiliate free booking notice" />
                </AffiliateFreeBookingMessage>
              )}

            {shouldCollectGuestInfo && !isBookingInvoice && (
              <GuestInformation
                stepNo={checkoutSteps.getStepNo('guestInfo') ?? 2}
                bookerGuestName={bookerGuestName}
                units={units}
                unitsLoaded={units.length > 0}
                onChange={handleAdditionalGuestChange}
                brandColor={partnerInfo.integration_brand_color}
                additionalGuests={formValues.additional_guests || []}
              />
            )}

            {shouldShowAdditionalInfo && (
              <AdditionalInformation
                questionsProps={{
                  questions,
                  onChange: handleQuestionChange,
                }}
                waiverProps={summaryProps.request.tour_waiver_info}
                showQuestions={shouldShowQuestions}
                showWaiver={shouldShowWaiver}
                stepNo={checkoutSteps.getStepNo('additionalInfo') ?? 2}
                isLiveBooking={isLiveBooking}
                fields={{
                  get: (fieldName: string) => {
                    return (
                      formValues[fieldName as keyof typeof formValues] || ''
                    );
                  },
                }}
                errors={{
                  get: (fieldName: string) => {
                    if (fieldName === 'checkout_questions') {
                      return transformCheckoutQuestionsErrors(
                        (errors.checkout_questions as
                          | { answer?: { message?: string } }[]
                          | undefined) || [],
                        formValues.checkout_questions || []
                      );
                    }

                    const error = errors[fieldName as keyof typeof errors];
                    return error ? [error.message || ''] : undefined;
                  },
                }}
                onWaiverChange={handleWaiverChange}
              />
            )}

            {shouldValidatePrivacyPolicy && (
              <Controller
                name="privacy_policy_accepted"
                control={control}
                render={({ field }) => (
                  <PrivacyPolicy
                    fields={{
                      get: () => field.value,
                    }}
                    errors={{
                      get: () => {
                        const error = errors.privacy_policy_accepted;
                        return error ? [error.message || ''] : undefined;
                      },
                    }}
                    onChange={(_fieldName: string, value: boolean) => {
                      field.onChange(value);
                    }}
                    privacy_policy_link={privacyPolicyLink}
                    privacy_policy_link_text={privacyPolicyLinkText}
                  />
                )}
              />
            )}
            <Divider />
          </ComponentContainer>
        </ColumnContainer>
        <ButtonContainer>
          <SubmitButton
            type="submit"
            color="primary"
            className={classnames({
              loading: isSubmitting,
              'brand-bg': true,
            })}
            disabled={isSubmitting}
            data-testid="checkout-page-one-submit"
          >
            {renderSubmitButtonLabel()}
          </SubmitButton>
        </ButtonContainer>
      </form>

      <PoweredBy hidePolicyLinks={context.hidePolicyLinks} />
    </PageContainer>
  );
};
